import { useEffect, useState } from "react";

import type { LobKey } from "src/jsonForms/preScreen/types";

import { routeLineOfBusiness } from "./registry";
import { resolveSearchConfig } from "./searchConfigResolver";
import type { SearchConfigFailure } from "./searchConfigResolver";
import type { SearchConfig } from "./types";

export type SearchConfigState =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly lob: LobKey; readonly config: SearchConfig }
  | { readonly status: "failed"; readonly failure: SearchConfigFailure };

/**
 * Resolves the Search_Config for a claims history route, one retrieval per
 * resolved line of business.
 */
export function useSearchConfig(pathname: string): SearchConfigState {
  /**
   * Keyed on the resolved line of business, so a change to a part of the path
   * the resolver ignores — a query string, a fragment, a trailing slash — does
   * not spend a second retrieval. An unregistered route resolves no line of
   * business and falls back to the path itself, so a move between two
   * unregistered paths still reports the path the user is on.
   */
  const resolutionKey: string = routeLineOfBusiness(pathname) ?? pathname;

  const [state, setState] = useState<SearchConfigState>({ status: "loading" });
  const [renderedKey, setRenderedKey] = useState(resolutionKey);

  // Discarded during render rather than in an effect, so a line-of-business
  // change cannot paint the previous line of business's table for a frame
  // before the loading indicator replaces it.
  if (renderedKey !== resolutionKey) {
    setRenderedKey(resolutionKey);
    setState({ status: "loading" });
  }

  useEffect(() => {
    let attached = true;

    void resolveSearchConfig(pathname).then((outcome) => {
      // A retrieval that settles after an unmount or a line-of-business change
      // belongs to a page the user has left, so its outcome is dropped rather
      // than written over the state the current route is showing.
      if (!attached) {
        return;
      }

      setState(
        outcome.ok
          ? { status: "ready", lob: outcome.lob, config: outcome.config }
          : { status: "failed", failure: outcome.failure }
      );
    });

    return () => {
      attached = false;
    };
    // Deliberately keyed on the resolved line of business alone. `pathname` is
    // read inside but is not a dependency: the parts of it that can change
    // without changing the key are the parts the resolver discards.
  }, [resolutionKey]);

  return state;
}
